import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  ArrowLeft,
  User as UserIcon,
  Headset,
  Send,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ErrorState from '../../components/common/ErrorState';
import { ListSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { fetchInquiriesByUserId } from '../../utils/api';
import { formatDateTime } from '../../utils/formatters';

const STATUS_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'pending', label: '답변대기' },
  { value: 'answered', label: '답변완료' },
];

/**
 * InquiryListPage - 내 문의 목록 및 상세(채팅형 스레드) 페이지
 */
const InquiryListPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailMobile, setShowDetailMobile] = useState(false); // 모바일 목록↔상세
  const [reply, setReply] = useState(''); // 추가 질문(mock)
  const [extraMessages, setExtraMessages] = useState({}); // inquiryId -> [msg]

  // 로그인 체크
  useEffect(() => {
    if (!user) {
      showToast('로그인이 필요한 서비스입니다.', 'warning');
      navigate('/login', { state: { from: '/support/inquiries' } });
    }
  }, [user, navigate, showToast]);

  const loadInquiries = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchInquiriesByUserId(user.id);
      setInquiries(data);
      if (data.length > 0) {
        setSelectedInquiry(data[0]);
      }
    } catch (error) {
      console.error('Failed to load inquiries:', error);
      setIsError(true);
      showToast('문의 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user, showToast]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const filteredInquiries = useMemo(() => {
    if (statusFilter === 'all') return inquiries;
    return inquiries.filter((i) => i.status === statusFilter);
  }, [inquiries, statusFilter]);

  if (!user) {
    return null;
  }

  const getStatusBadge = (status) => {
    if (status === 'answered') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle size={12} className="mr-1" />
          답변완료
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock size={12} className="mr-1" />
        답변대기
      </span>
    );
  };

  const handleSelect = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowDetailMobile(true);
    setReply('');
  };

  // 추가 질문 전송 (mock)
  const handleSendReply = () => {
    if (!reply.trim() || !selectedInquiry) return;
    setExtraMessages((prev) => ({
      ...prev,
      [selectedInquiry.id]: [
        ...(prev[selectedInquiry.id] || []),
        { from: 'user', text: reply.trim(), at: new Date().toISOString() },
      ],
    }));
    setReply('');
    showToast('추가 질문이 등록되었습니다.', 'success');
  };

  const renderDetail = () => {
    if (!selectedInquiry) {
      return (
        <Card className="h-full flex items-center justify-center">
          <p className="text-accent-500">문의를 선택해주세요</p>
        </Card>
      );
    }

    const threadExtra = extraMessages[selectedInquiry.id] || [];

    return (
      <Card>
        <div className="space-y-4">
          {/* 모바일 목록으로 돌아가기 */}
          <button
            onClick={() => setShowDetailMobile(false)}
            className="lg:hidden inline-flex items-center text-sm text-accent-600 hover:text-primary"
          >
            <ArrowLeft size={16} className="mr-1" />
            목록으로
          </button>

          {/* 문의 헤더 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              {getStatusBadge(selectedInquiry.status)}
              <span className="text-sm text-accent-500">
                {formatDateTime(selectedInquiry.createdAt)}
              </span>
            </div>
            <h2 className="text-xl font-bold text-primary-900 mb-2">
              {selectedInquiry.title}
            </h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent-100 text-accent-700">
              {selectedInquiry.category}
            </span>
          </div>

          {/* 채팅형 스레드 */}
          <div className="space-y-3 border-t border-accent-200 pt-4">
            {/* 내 문의 */}
            <div className="flex gap-2">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <UserIcon size={16} />
              </span>
              <div className="flex-1 rounded-lg rounded-tl-none bg-secondary-50 p-3">
                <p className="text-xs font-medium text-primary-700 mb-1">
                  {user.name} (나)
                </p>
                <p className="text-sm text-primary leading-relaxed whitespace-pre-line">
                  {selectedInquiry.content}
                </p>
              </div>
            </div>

            {/* 답변 */}
            {selectedInquiry.status === 'answered' && selectedInquiry.answer ? (
              <div className="flex flex-row-reverse gap-2">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                  <Headset size={16} />
                </span>
                <div className="flex-1 rounded-lg rounded-tr-none bg-green-50 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-xs font-medium text-green-800">
                      고객센터
                    </p>
                    <span className="text-xs text-accent-500">
                      {formatDateTime(selectedInquiry.answeredAt)}
                    </span>
                  </div>
                  <p className="text-sm text-primary leading-relaxed whitespace-pre-line">
                    {selectedInquiry.answer}
                  </p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-yellow-50 p-3">
                <p className="text-sm text-accent-600">
                  <Clock size={16} className="inline mr-1" />
                  답변 대기 중입니다. 영업일 기준 1-2일 내에 답변드리겠습니다.
                </p>
              </div>
            )}

            {/* 추가 질문(mock) */}
            {threadExtra.map((m, idx) => (
              <div key={idx} className="flex gap-2">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <UserIcon size={16} />
                </span>
                <div className="flex-1 rounded-lg rounded-tl-none bg-secondary-50 p-3">
                  <p className="text-xs font-medium text-primary-700 mb-1">
                    {user.name} (나)
                  </p>
                  <p className="text-sm text-primary leading-relaxed whitespace-pre-line">
                    {m.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* 추가 질문 입력 */}
          <div className="flex items-end gap-2 border-t border-accent-200 pt-4">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
              placeholder="추가로 궁금하신 내용을 입력해주세요"
              className="flex-1 resize-none rounded-lg border border-accent-300 px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSendReply}
              disabled={!reply.trim()}
            >
              <Send size={14} className="mr-1" />
              전송
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">내 문의</h1>
            <p className="text-sm text-accent-600">
              문의하신 내용과 답변을 확인하세요
            </p>
          </div>
          <Button variant="primary" onClick={() => navigate('/support/inquiry')}>
            <Plus size={16} className="mr-2" />
            새 문의 작성
          </Button>
        </div>

        {isLoading ? (
          <ListSkeleton rows={4} />
        ) : isError ? (
          <ErrorState
            title="문의 내역을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 후 다시 시도해주세요."
            onRetry={loadInquiries}
          />
        ) : inquiries.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto text-accent-400 mb-4" />
            <p className="text-accent-600 mb-4">아직 문의하신 내역이 없습니다.</p>
            <Button variant="primary" onClick={() => navigate('/support/inquiry')}>
              첫 문의 작성하기
            </Button>
          </div>
        ) : (
          <>
            {/* 상태 필터 */}
            <div className="mb-4 flex gap-2">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  aria-pressed={statusFilter === f.value}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                    statusFilter === f.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 문의 목록 */}
              <div
                className={`lg:col-span-1 space-y-3 ${
                  showDetailMobile ? 'hidden lg:block' : 'block'
                }`}
              >
                {filteredInquiries.length === 0 ? (
                  <p className="rounded-lg bg-accent-50 px-4 py-8 text-center text-sm text-accent-600">
                    해당 상태의 문의가 없습니다.
                  </p>
                ) : (
                  filteredInquiries.map((inquiry) => (
                    <Card
                      key={inquiry.id}
                      hover
                      onClick={() => handleSelect(inquiry)}
                      className={`cursor-pointer transition-all ${
                        selectedInquiry?.id === inquiry.id
                          ? 'ring-2 ring-primary-500 bg-primary-50'
                          : ''
                      }`}
                      padding="sm"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          {getStatusBadge(inquiry.status)}
                          <span className="text-xs text-accent-500">
                            {formatDateTime(inquiry.createdAt)}
                          </span>
                        </div>
                        <h3 className="font-medium text-primary-900 line-clamp-1">
                          {inquiry.title}
                        </h3>
                        <p className="text-xs text-accent-600 line-clamp-2">
                          {inquiry.content}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-accent-100 text-accent-700">
                          {inquiry.category}
                        </span>
                      </div>
                    </Card>
                  ))
                )}
              </div>

              {/* 문의 상세 */}
              <div
                className={`lg:col-span-2 ${
                  showDetailMobile ? 'block' : 'hidden lg:block'
                }`}
              >
                {renderDetail()}
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default InquiryListPage;
